import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/helpers";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const empty = { employee_id: "", project: "", date: today(), errors_found: 0, total_records: 100, rework_count: 0, verified_by: "R. Aadhya" };

const accColor = (a) => (a >= 98 ? "bg-emerald-100 text-emerald-800" : a >= 95 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700");

export default function QualityCheck() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = ["founder", "admin"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const { data: records = [], isLoading } = useQuery({ queryKey: ["quality"], queryFn: async () => (await api.get("/quality")).data });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data, enabled: isAdmin });
  const empName = (id) => employees.find((e) => e.employee_id === id)?.name || id;

  const create = useMutation({
    mutationFn: () => api.post("/quality", { ...form, errors_found: Number(form.errors_found), total_records: Number(form.total_records), rework_count: Number(form.rework_count) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["quality"] }); toast.success("Quality check recorded"); setOpen(false); setForm(empty); },
    onError: () => toast.error("Save failed"),
  });

  return (
    <div data-testid="quality-page">
      <PageHeader title="Quality Check" subtitle="Accuracy, errors, rework & quality score">
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-quality-btn" className="bg-navy hover:bg-navy-900 text-white rounded-lg"><Plus className="mr-2 h-4 w-4" /> Add Quality Check</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading text-navy">Record Quality Check</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Employee</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger data-testid="qc-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Project</Label><Input value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} /></div>
                <div><Label>Total Records</Label><Input type="number" value={form.total_records} onChange={(e) => setForm({ ...form, total_records: e.target.value })} /></div>
                <div><Label>Errors Found</Label><Input type="number" value={form.errors_found} onChange={(e) => setForm({ ...form, errors_found: e.target.value })} /></div>
                <div><Label>Rework Count</Label><Input type="number" value={form.rework_count} onChange={(e) => setForm({ ...form, rework_count: e.target.value })} /></div>
                <div><Label>Verified By</Label><Input value={form.verified_by} onChange={(e) => setForm({ ...form, verified_by: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={() => create.mutate()} data-testid="save-quality-btn" className="bg-navy text-white">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <SectionCard testId="quality-table-card">
        {isLoading ? <Skeleton className="h-64" /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {["Employee", "Project", "Date", "Errors", "Records", "Accuracy %", "Rework", "Quality Score", "Verified By"].map((h) => <TableHead key={h} className="text-navy font-semibold">{h}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((r) => (
                  <TableRow key={r.qc_id} data-testid={`quality-row-${r.qc_id}`} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{empName(r.employee_id)}</TableCell>
                    <TableCell>{r.project}</TableCell>
                    <TableCell>{fmtDate(r.date)}</TableCell>
                    <TableCell className="text-red-600 font-medium">{r.errors_found}</TableCell>
                    <TableCell>{r.total_records}</TableCell>
                    <TableCell><Badge variant="outline" className={accColor(r.accuracy)}>{r.accuracy}%</Badge></TableCell>
                    <TableCell>{r.rework_count}</TableCell>
                    <TableCell className="font-semibold text-navy">{r.quality_score}</TableCell>
                    <TableCell className="text-slate-500">{r.verified_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
